import { useState, useEffect, createContext, useContext, useCallback, ReactNode } from 'react';
import { type AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { AuthState } from '@/lib/auth';

// Create auth context
const AuthContext = createContext<{
  auth: AuthState;
  signInWithMagicLink: (email: string) => Promise<{ error: AuthError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
}>({
  auth: { user: null, session: null, isLoading: true, error: null },
  signInWithMagicLink: async () => ({ error: null }),
  signIn: async () => ({ error: null }),
  signUp: async () => ({ error: null }),
  signOut: async () => ({ error: null }),
  resetPassword: async () => ({ error: null }),
});

// Auth provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [auth, setAuth] = useState<AuthState>({
    user: null,
    session: null,
    isLoading: true,
    error: null,
  });

  // Initialize auth state
  useEffect(() => {
    // Get initial session
    const initializeAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const { data: { user } } = await supabase.auth.getUser();
        
        setAuth({
          user,
          session,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        setAuth({
          user: null,
          session: null,
          isLoading: false,
          error: error as AuthError,
        });
      }
    };

    initializeAuth();

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setAuth(prevState => ({
          ...prevState,
          user: session?.user ?? null,
          session,
          isLoading: false,
        }));
      }
    );

    // Clean up subscription
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Sign in with magic link
  const signInWithMagicLink = useCallback(async (email: string) => {
    try {
      // Auto-create accounts for @perfectvirtualteam.com emails
      const isAllowedDomain = email.toLowerCase().endsWith('@perfectvirtualteam.com');

      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          shouldCreateUser: isAllowedDomain,
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        }
      });

      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  }, []);

  // Sign in with password (kept for backward compatibility)
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  }, []);

  // Sign up
  const signUp = useCallback(async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
      });
      
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  }, []);

  // Reset password
  const resetPassword = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{ auth, signInWithMagicLink, signIn, signUp, signOut, resetPassword }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// Custom hook to use auth
export function useAuth() {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

export default useAuth;